import { Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatAccounting, formatAccountingPercent } from '@/lib/currency';
import type { JobCostOverviewHeaderDto } from '../types/jobCostOverview';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US');
};

interface CostOverviewHeaderProps {
  header: JobCostOverviewHeaderDto;
  onPrint: () => void;
  isPrinting: boolean;
}

export function CostOverviewHeader({ header, onPrint, isPrinting }: CostOverviewHeaderProps) {
  return (
    <div className="flex items-center justify-between bg-card border border-border rounded-lg px-4 py-2 mb-3">
      <div className="flex items-center gap-6 text-sm">
        <div>
          <span className="text-muted-foreground">Job:</span>{' '}
          <span className="font-semibold">{header.jobNumber}</span>
        </div>
        {header.dateOfLoss && (
          <div>
            <span className="text-muted-foreground">Date of Loss:</span> {formatDate(header.dateOfLoss)}
          </div>
        )}
        {header.claimNumber && (
          <div>
            <span className="text-muted-foreground">Claim #:</span> {header.claimNumber}
          </div>
        )}
        {header.claimDate && (
          <div>
            <span className="text-muted-foreground">Claim Date:</span> {formatDate(header.claimDate)}
          </div>
        )}
        <div>
          <span className="text-muted-foreground">Customer:</span> {header.customerName}
        </div>
        {header.assignedTo && (
          <div>
            <span className="text-muted-foreground">Project Manager:</span> {header.assignedTo}
          </div>
        )}
        {header.closedDate && (
          <div>
            <span className="text-muted-foreground">Closed:</span> {formatDate(header.closedDate)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 text-sm">
        <div>
          <span className="text-muted-foreground">Contract:</span>{' '}
          <span className="font-semibold tabular-nums">
            {formatAccounting(header.contractPrice)}
          </span>
        </div>
        <div>
          {/* Plain black, negatives in parentheses — the client asked us to drop
              the green/red profit colouring (Angie, Note 3). */}
          <span className="text-muted-foreground">Profit:</span>{' '}
          <span className="font-semibold tabular-nums">
            {formatAccountingPercent(header.profitPercent)}
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={onPrint} disabled={isPrinting}>
          <Printer className="h-4 w-4 mr-1" />
          Print
        </Button>
      </div>
    </div>
  );
}
